import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subject,
  Subscriber,
} from 'rxjs';
import { Inject, Injectable, Injector } from '@angular/core';
import { digitsFaToEn } from '@persian-tools/persian-tools';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSocket } from '../sockets/user.socket';
import { HttpClient } from '@angular/common/http';
import { map, timeout } from 'rxjs/operators';
import {
  UserLoginCredentials,
  UserRegisterCredentials,
  UserSchema,
} from '../models';
import { AuthStatusObject } from 'src/app/shared';
import { ConfigService, urls } from './config.service';
import { AuthFormComponent } from '../components';
import { UserService } from './user.service';
import { HTTP_REQUESTS_TIMEOUT, IS_SERVER_PLATFORM } from '../constant';
import { ActivationComponent } from '../components/activation/activation.component';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _googlePopup!: Window | null;
  public authStatus: BehaviorSubject<AuthStatusObject | any> =
    new BehaviorSubject(undefined);
  private _user!: UserSchema;
  public userProfile: BehaviorSubject<UserSchema> = new BehaviorSubject(
    {} as UserSchema
  );
  public userProfileObject: UserSchema | undefined = undefined;

  public userStatus = new BehaviorSubject<{ status: 'active' | 'notActive' }>({
    status: 'notActive',
  });
  public userStatusObject: { status: 'active' | 'notActive' } = {
    status: 'notActive',
  };

  constructor(
    private configService: ConfigService,
    private httpClient: HttpClient,
    private snakeBar: MatSnackBar,
    private dialogBox: MatDialog,
    private injector: Injector,
    private router: Router,
    @Inject(UserSocket) private userSocket: UserSocket,
    @Inject(IS_SERVER_PLATFORM) private isServer: boolean
  ) {
    this.userSocket.on('google-auth', (user: UserSchema) => {
      this.authStatus.next({
        isOk: true,
        data: user,
        type: 'google-auth',
      });

      this._googlePopup?.close();
    });
  }

  // get status$() {
  //   return this.authStatus.pipe(map((status) => this.mapStatus(status)));
  // }

  openUserActivationBoxDelay(delay: number) {
    setTimeout(() => {
      if (this.userProfileObject && this.userProfileObject.id) {
        if (this.dialogBox.openDialogs.length >= 1) return;

        this.dialogBox
          .open(ActivationComponent, {
            data: { user: this.userProfileObject },
            width: '350px',
            minHeight: '400px',
            minWidth: '50% !important',
          })
          .afterClosed()
          .subscribe((result: boolean) => {
            if (result) {
              this.snakeBar.open('کاربر با موفقیت تایید شد', '', {
                duration: 1000,
              });
              this.userStatus.next({ status: 'active' });
              this.authStatus.next({
                type: 'activation',
                data: {},
                isOk: true,
              });
            }
          });
      }
    }, delay);
  }

  openUserActivationBox(callback: (result: boolean) => void) {
    return this.userProfile.subscribe((user) => {
      if (this.dialogBox.openDialogs.length >= 1 || !!!user) return;

      this.dialogBox
        .open(ActivationComponent, {
          data: { user: user },
          width: '350px',
          minHeight: '400px',
          minWidth: '50% !important',
        })
        .afterClosed()
        .subscribe((result: boolean) => {
          callback(result);

          if (result) {
            this.snakeBar.open('کاربر با موفقیت تایید شد', '', {
              duration: 1000,
            });
            this.authStatus.next({ type: 'activation', data: {}, isOk: true });
          }
        });
    });
  }

  /**
   * This on authentication service.
   * @param authObject object that is generated on authentication
   */
  public updateUserOnAuthentication(authObject: AuthStatusObject) {
    // this.authStatus.next(authObject);
  }

  openAuthBox(route?: string): MatDialogRef<AuthFormComponent> | undefined {
    if (this.dialogBox.openDialogs.length >= 1) this.dialogBox.closeAll();

    return this.dialogBox.open(AuthFormComponent, {
      width: '350px',
      minHeight: '400px',
      minWidth: '50% !important',
    });
  }

  classicLogin(
    credentials: UserLoginCredentials
  ): Observable<AuthStatusObject> {
    credentials = this.toEnglish<typeof credentials>(credentials);
    const url = this.configService.getUrl('auth.local.login') as string;

    return new Observable((subscriber: Subscriber<AuthStatusObject>) => {
      this.httpClient
        .post(url, credentials, {
          withCredentials: true,
        })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe({
          next: (response) => {
            const authStatus: AuthStatusObject = {
              type: 'login',
              data: response,
              isOk: true,
            };
            subscriber.next(authStatus);
            this.authStatus.next(authStatus);
            this.getUserProfile(true);
          },
          error: (error) => {
            subscriber.error(error);
            this.errorHandler(this)(error);
          },
        });
    });
  }

  requestLoginCode(phone: string) {
    const url = this.configService.getUrl('auth.local.send-code') as string;
    const englishPhone = digitsFaToEn(phone);
    return new Observable((subscriber: Subscriber<any>) => {
      this.httpClient
        .post(url, {phone: englishPhone}, {
          withCredentials: true,
        })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe({
          next: (response) => {
            const loginCodestatus: AuthStatusObject = {
              type: 'login',
              data: response,
              isOk: true,
            };
            subscriber.next(loginCodestatus);
            // this.authStatus.next(authStatus);
            // this.getUserProfile(true);
          },
          error: (error) => {
            subscriber.error(error);
            this.errorHandler(this)(error);
          },
        });
    });
  }

  verifyLoginCode(phone: string, code: string) {
    const url = this.configService.getUrl('auth.local.verify-code') as string;
    const englishPhone = digitsFaToEn(phone);
    const englishCode = Number(digitsFaToEn(code));
    return new Observable((subscriber: Subscriber<any>) => {
      this.httpClient
        .post(url, {phone: englishPhone, code: englishCode}, {
          withCredentials: true,
        })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe({
          next: (response) => {
            const loginVerifyCodestatus: AuthStatusObject = {
              type: 'login',
              data: response,
              isOk: true,
            };
            subscriber.next(loginVerifyCodestatus);
            this.authStatus.next(loginVerifyCodestatus);
            this.getUserProfile(true);
          },
          error: (error) => {
            subscriber.error(error);
            this.errorHandler(this)(error);
          },
        });
    });
  }

  requestResetPassword(phone: string) {
    const url = urls.auth.forgetPassword;
    // const url = this.configService.getUrl('auth.local.send-code') as string;
    const englishPhone = digitsFaToEn(phone);
    return this.httpClient
    .post(url, {phone: englishPhone}, {
      withCredentials: true,
    })
    .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
  }

  verifyResetPasswordOtp(phone: string, code: string) {
    const url = urls.auth.verifyForgetPasswordOtp;

    const englishPhone = digitsFaToEn(phone);
    const codeEn = Number(digitsFaToEn(code));
    return this.httpClient
    .post(url, {phone: englishPhone, code: codeEn}, {
      withCredentials: true,
    })
    .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
  }

  setNewPassword(newPassword: string, resetPasswordToken: string) {
    const url = urls.auth.setNewPassword;

    return this.httpClient
    .put(url, {newPassword, resetPasswordToken}, {
      withCredentials: true,
    })
    .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
  } 

  classicRegister(
    credentials: UserRegisterCredentials
  ): Observable<AuthStatusObject> {
    const registerUrl = this.configService.getUrl('auth.local.register');

    return new Observable((subscriber: Subscriber<AuthStatusObject>) => {
      this.httpClient
        .post(registerUrl, this.toEnglish(credentials), {
          withCredentials: true,
        })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe((response) => {
          const registrationStatus: AuthStatusObject = {
            type: 'registration',
            data: response,
            isOk: true,
          };
          subscriber.next(registrationStatus);
          this.authStatus.next(registrationStatus);
        }, this.errorHandler(this));
    });
  }

  public logUserOut(): Observable<AuthStatusObject> {
    const logoutUrl = this.configService.getUrl('auth.local.logout');

    return new Observable((subscriber: Subscriber<AuthStatusObject>) => {
      this.httpClient
        .post(logoutUrl, {}, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe((response) => {
          const logoutStatus: AuthStatusObject = {
            type: 'logout',
            data: response,
            isOk: true,
          };
          subscriber.next(logoutStatus);
          this.authStatus.next(logoutStatus);
          if (!!!this.isServer) localStorage.removeItem('user');
          const _userService = this.injector.get(UserService);
          _userService.currentUser.next({});
          window.location.reload();
        }, this.errorHandler(this));
    });
  }

  public checkUserActivation(showActivationBox = false) {
    const url = this.configService.getUrl('activation.status');
    // return new Observable(
    //   (subscriber: Subscriber<{ status: 'active' | 'notActive' }>) => {
    this.httpClient
      .get(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      .subscribe({
        next: (response) => {
          const userStatus: { status: 'active' | 'notActive' } = response as {
            status: 'active' | 'notActive';
          };
          this.userStatus.next(userStatus);
          this.userStatusObject = userStatus;
          // subscriber.next(userStatus);
          if (showActivationBox && userStatus?.status === 'notActive')
            this.openUserActivationBoxDelay(10);
        },
        error: (error) => {
          this.userStatus.error(error?.error || error);
          this.userStatusObject = { status: 'notActive' };
          // subscriber.error(error);
        },
      });
    // }
    // );
  }

  async checkOrOpenAuthBox(callback?: (data?: any) => void) {
    const userIsLoggedIn = await this.isLoggedIn();

    if (!userIsLoggedIn) {
      const dialogRef = this.openAuthBox('login');

      dialogRef
        ?.afterClosed()
        .pipe(
          map((value, index: number) => {
            return value;
          })
        )
        .subscribe((data) => {
          if (callback) callback(data);

          this.authStatus.next(data);
          this.router.navigateByUrl('');
        });
    } else {
      this.snakeBar.open('شما از قبل وارد شده‌اید', 'باشه', {
        duration: 2000,
      });

      this.router.navigateByUrl('');
    }

    return userIsLoggedIn;
  }

  public googleAuth(): Observable<{ status: boolean; popup?: Window | null }> {
    const config = this.configService;

    const googleAuthPopupOptions =
      'top=50%, left=50%, height=500px, width=500px,toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no';
    const googleRequestUrl = config.getUrl('auth.google');

    return new Observable(
      (subscriber: Subscriber<{ status: boolean; popup?: Window | null }>) => {
        try {
          const socketId = this.userSocket.ioSocket.id;

          this._googlePopup = window.open(
            googleRequestUrl + `?socketId=${socketId}`,
            'auth-google',
            googleAuthPopupOptions
          );

          subscriber.next({
            popup: this._googlePopup,
            status: true,
          });
        } catch (googleAuthError) {
          throw googleAuthError;
        }
      }
    );
  }

  public getUserProfile(showActivationBox = false) {
    const url = this.configService.getUrl('user.profile');
    // new Observable((subscriber) => {
    this.httpClient
      .post<UserSchema>(url, {}, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      .subscribe({
        next: (result) => {
          this.userProfile.next(result as UserSchema);
          this.userProfileObject = result;
          // subscriber.next(result);
          if (showActivationBox) {
            this.checkUserActivation(true);
          }
        },
        error: (error) => {
          this.userProfile.next({} as UserSchema);
          this.userProfileObject = undefined;
          // subscriber.error(error?.error || error);
        },
      });
    // })
  }

  public async isLoggedIn(): Promise<boolean> {
    try {
      // this._user = await firstValueFrom(this.getUserProfile());
      return !!this.userProfileObject;
    } catch (e) {
      return false;
    }
  }

  public checkPhoneExistence(phoneNumber: string): Observable<boolean> {
    const phoneCheckUrl = this.configService.getUrl('user.phoneCheck');

    return new Observable((subscriber) => {
      const url = phoneCheckUrl + phoneNumber;

      this.httpClient
        .get(url)
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (result) => {
            subscriber.next(true);
          },
          (error) => {
            subscriber.next(false);
          }
        );
    });
  }

  private toEnglish<T>(data: any): T {
    for (let key in data) {
      data[key] = digitsFaToEn(data[key]);
    }

    return data;
  }

  private mapStatus(statusObject: AuthStatusObject): AuthStatusObject {
    const type = statusObject?.type;
    const _userService = this.injector.get(UserService);

    if (type === 'google-auth' || type === 'login') {
      _userService.isUserActivated(true).then((result) => {
        if (!result) {
          this.openUserActivationBoxDelay(10);
        }
      });
    }
    return statusObject;
  }

  //this function is unused & will be removed after test
  private newMapStatus(
    statusObject?: AuthStatusObject
  ): AuthStatusObject | undefined {
    const status = this.authStatus.getValue();
    // const _userService = this.injector.get(UserService);
    if (this.userProfileObject && this.userProfileObject.id) {
      if (status?.type === 'google-auth' || status?.type === 'login') {
        // _userService.isUserActivated(true).then((result) => {
        //   if (!result) {
        this.openUserActivationBoxDelay(10);
        // }
        // });
      }
    }
    return statusObject;
  }

  private errorHandler(that: AuthService): (error: Response | unknown) => void {
    return (error: Response | unknown) => {
      return null;
    };
  }
}
